import type { VoiceStatus } from "@/store/assistant-voice";
import { createAnswerSender } from "./voice-answer-sender";
import {
	type BargeInGate,
	isMeaningful,
	type TurnPhase,
} from "./voice-barge-in";
import {
	isAbort,
	sleep,
	TRANSCRIPT_POLL_MS,
	TRANSCRIPT_WAIT_MS,
} from "./voice-controller.config";
import type {
	VoiceControllerEvents,
	VoiceControllerOptions,
} from "./voice-controller.types";
import type { FillerTracker } from "./voice-filler";
import type { SettleWatcher } from "./voice-settle";
import { toSpokenPiece } from "./voice-spoken-numbers";
import type { VoiceStageLogger } from "./voice-stage-log";
import { createTurnTiming, type TurnTiming } from "./voice-timing";
import type { WaitSound } from "./voice-wait-sound";

/**
 * Giliran tanya-jawab suara: delegasi GPT-Live → Claude → jawaban per kalimat.
 * Setiap delegasi yang diterima diproses atau dicatat alasan diabaikannya
 * (#62). Giliran fallback (delegasi tak kunjung datang) menjawab sebagai teks
 * di panel; bila delegasinya datang terlambat, jawabannya ikut diucapkan.
 */

export interface TurnDeps {
	events: Pick<
		VoiceControllerEvents,
		"ask" | "timing" | "activity" | "transcript" | "fallbackAnswer"
	>;
	messages: Pick<
		VoiceControllerOptions,
		"failureCommentary" | "emptyCommentary"
	>;
	gate: BargeInGate;
	waitSound: WaitSound;
	filler: FillerTracker;
	settle: SettleWatcher;
	/** Kirim `session.commentary.append` untuk delegasi ini. */
	commentary(delegationId: string, content: string): void;
	setStatus(status: VoiceStatus): void;
	isClosed(): boolean;
	log: VoiceStageLogger;
}

export interface TurnRunner {
	phase(): TurnPhase;
	/** Audio GPT-Live terdengar pada `now` (untuk waktu tahap giliran berjalan). */
	audio(now: number): void;
	/** Hentikan giliran yang berjalan (barge-in). */
	bargeIn(): void;
	/** Delegasi GPT-Live diterima. */
	delegate(delegationId: string): Promise<void>;
	/** Delegasi tak datang: jawab ucapan terakhir sebagai teks. */
	fallback(): Promise<void>;
	stop(): void;
}

export function createTurnRunner(deps: TurnDeps): TurnRunner {
	const { events, gate, waitSound, filler, settle, log } = deps;
	let answering: AbortController | null = null;
	/** Giliran yang jawaban Claude-nya sudah lengkap (audio sedang selesai). */
	let received: AbortController | null = null;
	let timing: TurnTiming | null = null;
	/** Delegasi yang terikat ke giliran berjalan; null = giliran fallback belum punya delegasi. */
	let bound: string | null = null;
	let pending: string[] = [];
	/** Jawaban fallback yang sudah lengkap tetapi belum pernah diucapkan. */
	let unspoken: string | null = null;

	const phase = (): TurnPhase =>
		!answering ? "idle" : received === answering ? "settling" : "streaming";

	const say = (content: string) => {
		if (bound) deps.commentary(bound, content);
		else pending.push(content);
	};

	const bargeIn = () => {
		unspoken = null;
		if (!answering) return;
		log("barge_in", bound ? "bound" : "unbound");
		answering.abort();
		answering = null;
		timing = null;
		bound = null;
		pending = [];
		settle.stopAll();
		waitSound.cancel();
	};

	const takeUtterance = async () => {
		const until = performance.now() + TRANSCRIPT_WAIT_MS;
		while (!gate.text() && performance.now() < until && !deps.isClosed())
			await sleep(TRANSCRIPT_POLL_MS);
		const taken = gate.take(phase());
		events.transcript("");
		return taken;
	};

	const bind = (delegationId: string) => {
		bound = delegationId;
		// Kalimat yang ditahan ikut terkirim di bawah, jadi jawaban ini tidak perlu diucapkan lagi.
		unspoken = null;
		for (const content of pending) deps.commentary(delegationId, content);
		pending = [];
		log("delegation_late_bound", "running");
	};

	const speakUnspoken = (delegationId: string, answer: string) => {
		createAnswerSender({
			mode: "whole",
			send: (content) => deps.commentary(delegationId, content),
			isCancelled: () => deps.isClosed(),
			transform: toSpokenPiece,
		}).finish(answer);
		log("delegation_late_bound", "completed");
	};

	const run = async (
		question: string,
		turnTiming: TurnTiming,
		delegationId: string | null,
	) => {
		bargeIn();
		bound = delegationId;
		const turn = new AbortController();
		answering = turn;
		timing = turnTiming;
		deps.setStatus("answering");
		// Dulu `if (bound)`: giliran fallback tidak pernah mengarm bunyi tunggu,
		// padahal justru di situ penantiannya paling lama.
		waitSound.arm();
		const sender = createAnswerSender({
			mode: "sentence",
			send: say,
			isCancelled: () => turn.signal.aborted || deps.isClosed(),
			transform: toSpokenPiece,
			onFirstSend: () => {
				waitSound.sentenceReady();
				filler.close();
				turnTiming.sent();
			},
		});
		try {
			const answer = await events.ask(
				question,
				{
					onStatus: () => {
						turnTiming.toolStarted();
						sender.toolStarted();
					},
					onDelta: (text) => {
						turnTiming.token();
						sender.delta(text);
						events.activity();
					},
				},
				turn.signal,
			);
			sender.finish(answer.text);
			if (answering === turn) received = turn;
			if (answering === turn && !bound) unspoken = answer.text;
			log("delegation_answered", bound ? "bound" : "fallback");
			turnTiming.answered(answer.messageId);
			settle.wait(performance.now(), () => {
				if (answering !== turn) return;
				answering = null;
				timing = null;
				turnTiming.settled();
				filler.clear();
				deps.setStatus("ready");
			});
		} catch (err) {
			sender.cancel();
			waitSound.cancel();
			// Barge-in sudah mengganti giliran (status "Mendengarkan"); selain itu kembali siap.
			if (answering !== turn || deps.isClosed()) return;
			answering = null;
			timing = null;
			filler.clear();
			if (!isAbort(err)) {
				log("delegation_failed", bound ? "bound" : "fallback");
				say(deps.messages.failureCommentary);
			}
			deps.setStatus("ready");
		}
	};

	const delegate = async (delegationId: string) => {
		log("delegation_received");
		if (answering && !bound) return bind(delegationId);
		if (unspoken !== null) {
			const answer = unspoken;
			unspoken = null;
			return speakUnspoken(delegationId, answer);
		}
		const turnTiming = createTurnTiming(() => performance.now(), events.timing);
		const { text, verdict, reason } = await takeUtterance();
		if (deps.isClosed()) return;
		// Delegasi dari gema/kata ragu tidak dijawab: giliran yang berjalan dibiarkan selesai.
		if (verdict === "ignore") return log("delegation_ignored", reason);
		if (!text) {
			filler.close();
			deps.commentary(delegationId, deps.messages.emptyCommentary);
			deps.setStatus("ready");
			return log("delegation_ignored", "empty_utterance");
		}
		await run(text, turnTiming, delegationId);
	};

	const fallback = async () => {
		if (deps.isClosed() || answering) return log("fallback_skipped", "busy");
		const turnTiming = createTurnTiming(() => performance.now(), events.timing);
		const { text, verdict, reason } = gate.take(phase());
		events.transcript("");
		if (verdict === "ignore" || !text)
			return log("fallback_skipped", reason === "none" ? "empty" : reason);
		if (!isMeaningful(text, 0))
			return log("fallback_skipped", "not_meaningful");
		log("fallback_started");
		events.fallbackAnswer();
		await run(text, turnTiming, null);
	};

	return {
		phase,
		audio: (now) => timing?.audio(now),
		bargeIn,
		delegate,
		fallback,
		stop() {
			answering?.abort();
			answering = null;
			bound = null;
			pending = [];
		},
	};
}
